import { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface UseMercadoPagoPollingParams {
  session: Session | null;
  loadUserData: (userId: string) => Promise<void>;
  lastLoadedIdRef: React.MutableRefObject<string | null>;
}

export interface UseMercadoPagoPollingReturn {
  isActivatingPro: boolean;
}

export function useMercadoPagoPolling({
  session,
  loadUserData,
  lastLoadedIdRef
}: UseMercadoPagoPollingParams): UseMercadoPagoPollingReturn {
  const [isActivatingPro, setIsActivatingPro] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status') || params.get('collection_status');

    if (session?.user && (status === 'approved' || status === 'pending')) {
      const userId = session.user.id;
      
      // Limpa os parâmetros de busca da URL imediatamente
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      if (status === 'approved') {
        setIsActivatingPro(true);
        let attempts = 0;
        const maxAttempts = 10;
        
        const poll = async () => {
          if (cancelled) return;
          attempts++;
          try {
            interface SubscriptionStatusResponse {
              status: string;
              plan_type?: string;
            }

            const { data: subData, error } = await supabase
              .from('subscriptions')
              .select('status, plan_type')
              .eq('user_id', userId)
              .maybeSingle() as unknown as { data: SubscriptionStatusResponse | null; error: Error | null };

            if (cancelled) return;

            if (!error && subData && subData.status === 'active') {
              setIsActivatingPro(false);
              // Força o recarregamento dos dados do usuário
              lastLoadedIdRef.current = null;
              await loadUserData(userId);
              const planName = subData.plan_type === 'especialista' || subData.plan_type === 'especialista_anual' ? 'Especialista Premium' : 'Pro';
              alert(`Parabéns! Seu plano R-MAX ${planName} foi ativado com sucesso. Aproveite todos os recursos!`);
              return;
            } else if (attempts >= maxAttempts) {
              setIsActivatingPro(false);
              alert('Seu pagamento foi aprovado, mas a ativação do plano está demorando um pouco mais do que o esperado. O plano deve ser ativado automaticamente nos próximos minutos.');
              return;
            }
          } catch (err: unknown) {
            console.error('Error polling subscription status:', err);
            if (attempts >= maxAttempts) {
              setIsActivatingPro(false);
              return;
            }
          }

          if (!cancelled) {
            timer = setTimeout(poll, 3000);
          }
        };

        poll();
      } else if (status === 'pending') {
        alert('Seu pagamento está em processamento ou análise pelo Mercado Pago. Seu plano será ativado automaticamente assim que for confirmado!');
      }
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [session, loadUserData, lastLoadedIdRef]);

  return { isActivatingPro };
}
